import React, {useState} from "react";
import axios from "axios";
import {Link, useNavigate} from "react-router-dom";
import {Alert, Button, Container, Form, FormFeedback, FormGroup, Input, Label, Spinner} from "reactstrap";
import {parseApiError} from "../../shared/utils/apiErrors";
import IconLabel from "../../shared/components/IconLabel";
import {KeyIcon, XIcon} from "../../shared/components/icons";

const PASSWORD_URL = "/os/api/me/password/";
const BACK = "/config";

const THROTTLED = "Muitas tentativas. Tente novamente mais tarde.";

// The logged-in user changes their own password. The current one is asked for
// again so a stolen session alone can't take over the account.
const PasswordChange = () => {
    const navigate = useNavigate();
    const [values, setValues] = useState({current_password: "", new_password: "", confirm_password: ""});
    const [errors, setErrors] = useState({general: null, fields: {}});
    const [submitting, setSubmitting] = useState(false);

    const handleChange = ({target: {name, value}}) => setValues((v) => ({...v, [name]: value}));

    const mismatch = values.confirm_password !== "" && values.confirm_password !== values.new_password;
    const incomplete = Object.values(values).some((v) => v === "");

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors({general: null, fields: {}});
        setSubmitting(true);
        try {
            await axios.post(PASSWORD_URL, {
                current_password: values.current_password,
                new_password: values.new_password,
            });
            navigate(BACK, {replace: true, state: {passwordChanged: true}});
        } catch (error) {
            setErrors(
                error.response?.status === 429
                    ? {general: THROTTLED, fields: {}}
                    : parseApiError(error, "Não foi possível alterar a senha. Tente novamente.")
            );
            setSubmitting(false);
        }
    };

    const field = (name, label, autoComplete) => (
        <FormGroup>
            <Label for={name}>{label}</Label>
            <Input
                id={name}
                name={name}
                type="password"
                autoComplete={autoComplete}
                value={values[name]}
                onChange={handleChange}
                invalid={Boolean(errors.fields[name]) || (name === "confirm_password" && mismatch)}
            />
            <FormFeedback>
                {errors.fields[name] ?? (name === "confirm_password" ? "As senhas não coincidem." : "")}
            </FormFeedback>
        </FormGroup>
    );

    return (
        <Container className="mt-4 mb-5" style={{maxWidth: 640}}>
            <Link to={BACK}>&larr; Voltar</Link>
            <h3 className="mt-3 mb-3">Alterar senha</h3>
            <Form onSubmit={handleSubmit} noValidate>
                {errors.general && <Alert color="danger">{errors.general}</Alert>}
                {field("current_password", "Senha atual", "current-password")}
                {field("new_password", "Nova senha", "new-password")}
                {field("confirm_password", "Confirmar nova senha", "new-password")}
                <div className="d-flex justify-content-end gap-2">
                    <Button
                        color="secondary"
                        outline
                        type="button"
                        onClick={() => navigate(BACK)}
                        disabled={submitting}
                    >
                        <IconLabel icon={<XIcon />}>Cancelar</IconLabel>
                    </Button>
                    <Button color="primary" type="submit" disabled={submitting || incomplete || mismatch}>
                        {submitting ? <Spinner size="sm" /> : <IconLabel icon={<KeyIcon />}>Alterar senha</IconLabel>}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default PasswordChange;
